import { QuestionAnswerCard } from '@ninjacart/nc-nk-adaptive-cards';
import {
  createCommentQnA,
  getQnACommentList,
  onQnAReplyLike,
  QnAPlaceHolder,
  useQnaFeedStore,
} from '@ninjacart/nc-nk-advisory';
import { useEventZStore, UserProps } from '@ninjacart/nc-nk-store';
import { Spinner, View } from '@ninjacart/nc-nk-ui';
import { BannerAds, useIsFocused } from '@ninjacart/nc-nk-web-deps';
import { FlashList } from '@shopify/flash-list';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions } from 'react-native';
import useUserEngagementStore from '../store/useUserEngagementStore';
import {ProfileTabEmptyState} from './profileTabEmptyState';

type QnaParams = {
  userProfile: UserProps;
  id?: string;
  hasUserLoggedIn: boolean | null;
};

const AllQnaList = ({ userProfile, id, hasUserLoggedIn }: QnaParams) => {
  const { t } = useTranslation();
  const startCountRef = useRef({ page: 1, limit: 10 });
  const [refreshingQna, setRefreshingQna] = useState(false);
  const [onEndReachedSpinnerQna, setOnEndReachedSpinnerQna] = useState(false);
  const isFocused = useIsFocused();
  const userQnaFeed = useUserEngagementStore((state) => state.userQnaFeed);
  const isQnaLoaded = useUserEngagementStore((state) => state.isQnaLoaded);
  const fetchQuestionData = useUserEngagementStore((state) => state.fetchQuestionData);
  const resetQnaFeed = useUserEngagementStore((state) => state.resetQnaFeed);
  const userId = id ?? userProfile?.user?.id!;
  const realmId = userProfile?.realm?.id ?? '1';
  const deviceWidth = Dimensions.get('window').width;
  const bannerAdKeywords = useEventZStore((state) => state.bannerAdKeywords);

  const qnaPayloadRef = useRef({
    userId: userId!,
    realmId: realmId,
    limit: startCountRef.current.limit,
    page: startCountRef.current.page,
    desc: true,
    includeReplies: true,
  });

  useEffect(() => {
    if (!isFocused) {
      return;
    }
    if (isFocused) {
      resetQnaFeed();
      startCountRef.current.page = 1;
      qnaPayloadRef.current.page = 1;
      fetchQuestionData(qnaPayloadRef.current, false);
    }
  }, [isFocused]);

  const onPullToRefreshQna = async () => {
    setRefreshingQna(true);
    resetQnaFeed();
    startCountRef.current.page = 1;
    qnaPayloadRef.current.page = 1;
    try {
      await fetchQuestionData(qnaPayloadRef.current, true);
    } catch (err) {
      console.log('err qna list onPullToRefreshQna', err);
    } finally {
      setRefreshingQna(false);
    }
  };

  const onEndReachedQna = async () => {
    startCountRef.current.page += 1;
    qnaPayloadRef.current.page = startCountRef.current.page;
    setOnEndReachedSpinnerQna(true);
    try {
      await fetchQuestionData(qnaPayloadRef.current, false);
    } catch (err) {
      console.log('err qna list onEndReachedQna', err);
    } finally {
      setOnEndReachedSpinnerQna(false);
    }
  };

  const myQnAListEmptyComponent = () =>
    isQnaLoaded &&
    !userQnaFeed?.length && (
      <ProfileTabEmptyState emptyStateContent={t('page.youHavenotAskedAnyQuestion')} />
    );

  const renderMyQnAListItem = ({ item, index }) => {
    switch (item?.type) {
      case 'BANNER_AD':
        return (
          <BannerAds
            deviceWidth={deviceWidth}
            itemKey={item?.actionData?.content?.itemKey ?? index.toString()}
            unitId={item?.actionData?.content?.unitId}
            adSizeDefs={item?.actionData?.content?.adSizeDefs ?? []}
            bannerAdKeywords={bannerAdKeywords}
            screenName={' profile qna list page'}
          />
        );

      default:
        return (
          <QuestionAnswerCard
            item={item?.actionData?.content}
            index={index}
            questionId={''}
            // expertAnswer={item?.actionData?.content?.children?.find((x) => x.creatorRole === 'mods')}
            pageName="profile detail"
            useQnaFeedStore={useQnaFeedStore}
            createCommentQnA={createCommentQnA}
            getQnACommentList={getQnACommentList}
            onQnAReplyLike={onQnAReplyLike}
          />
        );
    }
  };

  if (!isQnaLoaded) {
    return <QnAPlaceHolder showDetailPage={true} />;
  }

  return (
    <View flex={1} backgroundColor={'$background'}>
      <FlashList
        data={userQnaFeed}
        renderItem={renderMyQnAListItem}
        keyExtractor={(item, index) => `${item?.actionData?.content.id}-${index}`}
        showsHorizontalScrollIndicator={false}
        estimatedItemSize={224}
        showsVerticalScrollIndicator={false}
        refreshing={refreshingQna}
        onRefresh={onPullToRefreshQna}
        ListEmptyComponent={myQnAListEmptyComponent}
        onEndReachedThreshold={0.5}
        onEndReached={onEndReachedQna}
        ListFooterComponent={() =>
          onEndReachedSpinnerQna && (
            <View style={{ flex: 1, padding: 10, alignItems: 'center' }}>
              <Spinner size="large" color="$green10" />
            </View>
          )
        }
        ListFooterComponentStyle={{ height: 100 }}
      />
    </View>
  );
};

export default AllQnaList;
